import React from 'react';
import styled from 'styled-components';
import { Text } from 'components/Typography';
import { theme, toRem } from 'design';
import { CalculatorSplitMethod, IBeneficiary, ICalculatorParticipant, ICalculatorRule, ICalculatorRuleRemainder, ICalculatorRuleSharePolicyShare, ShareEligibilityMethod, ShareRemainderType, ShareRuleType } from 'types';
import { getTipSources, toPercent, toTitleCase } from 'utils';

const Column = styled.div`
  display: flex;
  flex-flow: column nowrap;
`;

const DetailsContainer = styled(Column)`
  padding: ${toRem(16)};
  border-radius: ${toRem(theme.borderRadius.small)};
  background-color: ${theme.color.neutral[50]};
  gap: ${toRem(12)};

  p {
    line-height: 165%;
  }

  ul {
    margin-left: ${toRem(16)};
  }
`;

const DetailsText = styled(Text)`
  color: ${theme.color.neutral[500]};
`;

const Semibold = styled.span`
  font-weight: ${theme.font.weight.semiBold};
`;

const partitionBeneficiaries = (benefiting: IBeneficiary[]) =>
  benefiting.reduce(
    (partitions, beneficiary) => {
      switch (beneficiary.split) {
        case CalculatorSplitMethod.TIME_WORKED:
          partitions.timeWorked.push(toTitleCase(beneficiary.name));
          break;
        case CalculatorSplitMethod.NONE:
          partitions.none.push(toTitleCase(beneficiary.name));
          break;
        default:
          partitions.evenly.push(toTitleCase(beneficiary.name));
          break;
      }
      return partitions;
    },
    { timeWorked: [], evenly: [], none: [] }
  );

const mapBenefitingJobs = (shares: ICalculatorRuleSharePolicyShare[]) => {
  if (!shares?.length)
    return (
      <DetailsText size="xsmall" fontWeight="semibold">
        None
      </DetailsText>
    );

  return (
    <ul>
      {shares.map((shareItem, i) => {
        const { timeWorked, evenly, none } = partitionBeneficiaries(shareItem.beneficiaries);
        return (
          <DetailsText size="xsmall" key={`share-${i}`}>
            <li>
              <Semibold>{toPercent(shareItem.share)}</Semibold> {'of '}
              <Semibold>{shareItem.type === ShareRuleType.TIP ? getTipSources(shareItem.sources) : toTitleCase(shareItem.category)}</Semibold>
              {` per ${shareItem.eligibilityMethod === ShareEligibilityMethod.BY_TICKET ? 'check' : 'segment'} to `}
              {timeWorked.length > 0 && (
                <>
                  <Semibold>{timeWorked.join(' & ')}</Semibold> split by time worked{evenly.length > 0 ? ', ' : ''}
                </>
              )}
              {evenly.length > 0 && (
                <>
                  <Semibold>{evenly.join(' & ')}</Semibold> split evenly
                </>
              )}
              {none.length > 0 && <Semibold>{none.join(' & ')}</Semibold>}
            </li>
          </DetailsText>
        );
      })}
    </ul>
  );
};

const mapRemainderJobs = (remainder: ICalculatorRuleRemainder, isAdvanced?: boolean) => (
  <>
    <DetailsText size="xsmall" fontWeight="semibold">
      {remainder.type === ShareRemainderType.SELF ? 'Self' : 'Pooled'}
    </DetailsText>
    {remainder.type === ShareRemainderType.POOL && (
      <ul>
        {remainder.beneficiaries.map((beneficiary, i) => (
          <DetailsText size="xsmall" key={`remainder-${i}`}>
            <li>
              <Semibold>{beneficiary.participants.map((participant) => toTitleCase(participant.name)).join(' & ')}</Semibold>
              {isAdvanced && (
                <>
                  {' earning '}
                  <Semibold>
                    {beneficiary.conditions?.[0]?.hours ? `${beneficiary.conditions[0].hours} hour${beneficiary.conditions[0].hours !== 1 ? 's' : ''} of ` : ''}
                    {getTipSources(beneficiary.sources)}
                  </Semibold>
                  {' split'} {beneficiary.split === CalculatorSplitMethod.TIME_WORKED ? 'by time worked' : 'evenly'}
                </>
              )}
            </li>
          </DetailsText>
        ))}
      </ul>
    )}
  </>
);

const mapDefaultPoolJobs = (jobs: ICalculatorParticipant[]) => (
  <ul>
    {jobs.map((job, i) => (
      <DetailsText size="xsmall" key={`job-${i}`}>
        <li>
          <Semibold>{toTitleCase(job?.name)}</Semibold>
        </li>
      </DetailsText>
    ))}
  </ul>
);

interface ITipShareBreakdownProps {
  tipShare: ICalculatorRule;
  isAdvanced?: boolean;
  className?: string;
}

const TipShareBreakdown = ({ tipShare, isAdvanced, className }: ITipShareBreakdownProps) => (
  <DetailsContainer data-cy="rules-details" className={className}>
    {tipShare.default ? (
      <>
        {tipShare.includeNoLabor && (
          <Text size="xxsmall" fontWeight="medium">
            Tips included without timecards
          </Text>
        )}
        {tipShare.contributor && (
          <Column>
            <Text size="xxsmall" fontWeight="medium">
              Contributing Jobs:
            </Text>
            {mapDefaultPoolJobs([tipShare.contributor, ...tipShare.additionalContributors])}
          </Column>
        )}
        <Column>
          <Text size="xxsmall" fontWeight="medium">
            Receiving Jobs:
          </Text>
          {tipShare.sharePolicy?.remainder && mapDefaultPoolJobs(tipShare.sharePolicy.remainder.beneficiaries.map((beneficiary) => beneficiary.participants[0]))}
        </Column>
      </>
    ) : (
      <>
        <Column>
          <Text size="xxsmall" fontWeight="medium">
            Benefits:
          </Text>
          {mapBenefitingJobs(tipShare.sharePolicy?.shares)}
        </Column>
        <Column>
          <Text size="xxsmall" fontWeight="medium">
            Remainder:
          </Text>
          {tipShare.sharePolicy?.remainder && mapRemainderJobs(tipShare.sharePolicy.remainder, isAdvanced)}
        </Column>
      </>
    )}
  </DetailsContainer>
);

export default TipShareBreakdown;
